import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, mergeMap, Observable, of, tap } from 'rxjs';

import { DigitalSignatureAction, SignRequestStatus } from '@dpr/enums';
import { DprCard } from '@dpr/models';
import {
  DprReportSignRequestStatus,
  Signer,
  SignerGridData,
} from '@dpr/models/digital-signature';
import { ConfirmDialogComponent } from '@shared/components';
import { StaticMessage } from '@shared/enums';
import { GridApi, GridColumn, GridOptions, GridSync } from '@grid/models';
import { Constants, getStaticMessage } from '@shared/utils';
import { ImageCompressionType } from '@shared/enums/dpr';
import { BaseDialogComponent } from '@shared/models/component';
import { GridComponent } from '@grid/grid.component';
import { TextDisplayComponent, TextEditComponent } from '@grid/components';
import { GridSortMode } from '@grid/enums';
import { GridColumns, GridDefaultLabels } from '@shared/utils/grid';
import { canDeactivateUnsavedChanges } from '@shared/guards';

@UntilDestroy()
@Component({
  selector: 'app-digital-signature-modal',
  templateUrl: './digital-signature-modal.component.html',
  styleUrls: ['./digital-signature-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DigitalSignatureModalComponent
  extends BaseDialogComponent
  implements OnInit
{
  @ViewChild('statusTemplate', { static: true })
  statusTemplate: TemplateRef<any>;

  @ViewChild('statusChangedOnTemplate', { static: true })
  statusChangedOnTemplate: TemplateRef<any>;

  @ViewChild(GridComponent)
  private gridComponent: GridComponent<SignerGridData>;

  gridEntityName = $localize`:@@dpr.digitalSignatureModal.txtSigner:Signer`;
  imageCompressionType: number;
  documentStatusChangedOn: Date;
  shouldDisplayMessageColumn = false;
  isInitialized = false;

  SignRequestStatus: typeof SignRequestStatus = SignRequestStatus;

  isDownloadImageAtOriginalSize = false;

  gridOptions: GridOptions<SignerGridData>;
  gridColumns: GridColumn<SignerGridData>[];

  predefinedChanges: GridSync<SignerGridData>;

  gridData$: Observable<SignerGridData[]> =
    this.data.existingSignRequestData$?.pipe(
      mergeMap((data) => {
        return of(
          data?.signers.map((signer) => ({
            ...signer,
            id: signer.email,
          })) ?? []
        );
      })
    );

  predefinedChanges$: Observable<GridSync<SignerGridData>> =
    this.data.previousSigners$?.pipe(
      mergeMap((emails) => {
        return of({
          addedList: emails.map((x) => ({ ...x, id: x.email })),
          updatedList: [],
          deletedIds: [],
        });
      }),
      tap((changes) => {
        this.predefinedChanges = changes;
      })
    );

  constructor(
    protected dialogRef: MatDialogRef<DigitalSignatureModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      dprCard: DprCard;
      title: string;
      existingSignRequestData$: Observable<DprReportSignRequestStatus>;
      previousSigners$: Observable<Signer[]>;
    },
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
    super(dialogRef);
  }

  private get isDirty(): boolean {
    return this.gridComponent.api.isDirty();
  }

  ngOnInit(): void {
    this.gridOptions = {
      labeling: GridDefaultLabels,
      addConfig: {
        value: {
          email: '',
          canBeDeleted: true,
        },
        buttonPosition: 'header',
        allowAdding: !this.data.dprCard.hasActiveSignRequest,
      },
      editConfig: {
        allowEditing: !this.data.dprCard.hasActiveSignRequest,
      },
      deleteConfig: {
        allowDeleting: !this.data.dprCard.hasActiveSignRequest,
      },
      saveConfig: {
        isExternalSave: true,
      },
      rowConfig: {
        allowDeleting: true,
        allowSelection: true,
        isEmpty: this.isRowEmpty.bind(this),
      },
      sortConfig: {
        mode: GridSortMode.Fixed,
      },
      selectionConfig: {
        allowSelection: !this.data.dprCard.hasActiveSignRequest,
      },
      events: {
        onGridRegistered: (gridApi: GridApi<SignerGridData>) => {
          if (this.predefinedChanges) {
            gridApi.addRows(...this.predefinedChanges.addedList);
            this.cdr.detectChanges();
          }
        },
      },
    };

    this.gridColumns = [
      {
        columnDef: 'email',
        header: {
          name: $localize`:@@dpr.digitalSignatureModal.colEmail:Email`,
        },
        minWidth: 200,
        display: { type: TextDisplayComponent },
        edit: { type: TextEditComponent },
      },
    ];

    this.data.existingSignRequestData$
      .pipe(
        untilDestroyed(this),
        tap((data: DprReportSignRequestStatus) => {
          if (!data) {
            this.defineGridOptions();
            return;
          }

          this.documentStatusChangedOn = data.statusChangedOn
            ? new Date(data.statusChangedOn)
            : null;

          if (
            data.signers.some(
              (signer) =>
                signer?.message && signer?.status === SignRequestStatus.Declined
            )
          ) {
            this.shouldDisplayMessageColumn = true;
          }

          this.defineGridOptions();
        })
      )
      .subscribe();
  }

  onCancel(): void {
    if (!this.isDirty) {
      this.dialogRef.close();
      return;
    }

    canDeactivateUnsavedChanges(this, this._dialog)
      .pipe(
        untilDestroyed(this),
        filter((val) => val),
        tap(() => {
          this.dialogRef.close();
        })
      )
      .subscribe();
  }

  onCancelRequest(): void {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      ...Constants.DialogOptions,
      data: {
        title: $localize`:@@dpr.digitalSignatureModal.txtConfirmCancelESignRequest:Are you sure you want to cancel this e-sign request?`,
        message: $localize`:@@dpr.digitalSignatureModal.txtCancelESignRequest:Once the request is cancelled you will lose the current signing progress and you will be able to create a new e-sign request.`,
        buttonLabel: $localize`:@@dpr.digitalSignatureModal.btnCancelThisRequest:Yes, cancel this request`,
        buttonColor: 'warn',
        buttonIcon: 'close',
        cancelButtonLabel: $localize`:@@dpr.digitalSignatureModal.btnDontCancel:Don't cancel`,
      },
    });

    confirmDialog
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter((result) => !!result),
        tap(() => {
          this.dialogRef.close({
            action: DigitalSignatureAction.Cancel,
            emails: null,
            imageCompressionType: null,
          });
        })
      )
      .subscribe();
  }

  isRowEmpty(item: any): boolean {
    return String.isNullOrWhiteSpace(item?.email);
  }

  onSend(): void {
    if (!this.validateChanges()) {
      return;
    }

    if (!this.gridComponent.api.hasChanges()) {
      this.dialogRef.close(null);
      return;
    }

    const data = this.gridComponent.api.getChanges();

    if (!data.addedList.length) {
      this.dialogRef.close(null);
      return;
    }

    this.dialogRef.close({
      action: DigitalSignatureAction.Send,
      emails: data.addedList.map((a: Partial<SignerGridData>) => a.email),
      imageCompressionType: this.isDownloadImageAtOriginalSize
        ? ImageCompressionType.None
        : ImageCompressionType.ResizeToFit,
    });
  }

  //#region Deactivation guard

  canDeactivate(): boolean | Observable<boolean> {
    return !this.isDirty;
  }

  save(): Observable<boolean> {
    this.onSend();
    return of(false);
  }

  //#endregion

  //#region Private methods

  private defineGridOptions(): void {
    if (this.data.dprCard?.hasActiveSignRequest) {
      this.gridColumns.push(
        {
          columnDef: 'status',
          header: {
            name: $localize`:@@dpr.digitalSignatureModal.colStatus:Status`,
          },
          allowEditing: false,
          minWidth: 120,
          display: {
            type: this.statusTemplate,
          },
          tabIndex: -1,
          disableFocusedBackground: true,
        },
        {
          columnDef: 'statusChangedOn',
          header: {
            name: $localize`:@@dpr.digitalSignatureModal.colLastStatusChange:Last Status Change`,
          },
          allowEditing: false,
          minWidth: 175,
          display: {
            type: this.statusChangedOnTemplate,
          },
          tabIndex: -1,
          disableFocusedBackground: true,
        }
      );

      if (this.shouldDisplayMessageColumn) {
        this.gridColumns.push({
          columnDef: 'message',
          header: {
            name: $localize`:@@dpr.digitalSignatureModal.colMessage:Message`,
          },
          minWidth: 100,
          display: {
            type: TextDisplayComponent,
          },
          tabIndex: -1,
          disableFocusedBackground: true,
        });
      }
    } else {
      this.gridColumns.push(GridColumns.delete);
    }

    this.isInitialized = true;
  }

  private validateChanges(): boolean {
    const changes = this.gridComponent?.api?.getChanges();
    const entities = [...changes.addedList, ...changes.updatedList];

    if (this.gridComponent.data.length > 20) {
      this.dialog.open(ConfirmDialogComponent, {
        ...Constants.DialogOptions,
        data: {
          extra: this.gridEntityName,
          title: getStaticMessage(StaticMessage.ValidationErrorTitle),
          message: getStaticMessage(
            StaticMessage.EntitiesCountExceeded,
            $localize`:@@dpr.digitalSignatureModal.errSignersCount:20 signers`
          ),
          buttonLabel: getStaticMessage(StaticMessage.OkButton),
          hideCancelButton: true,
        },
      });

      return false;
    }

    const duplicates = this.gridComponent.data
      .filter((x) => !this.isRowEmpty(x))
      .getDuplicatesByProperties('email');

    for (const entity of entities) {
      const errorMessages = [];

      if (!entity.email.trim()) {
        errorMessages.push(
          $localize`:@@dpr.digitalSignatureModal.errEmailMandatory:- Email field is mandatory.`
        );
      }

      if (duplicates.includesDuplicatedValues(entity.email)) {
        errorMessages.push(
          $localize`:@@dpr.digitalSignatureModal.errDuplicateEmail:Please define each email address only once.`
        );
      }

      const emailRegex = new RegExp(
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );

      if (!emailRegex.test(entity.email.trim())) {
        errorMessages.push(
          $localize`:@@dpr.digitalSignatureModal.errEmailAddressField:- Email address field: <b>${entity.email}:email:</b> is not a valid email address. Please provide a valid email address.`
        );
      }

      if (errorMessages.length) {
        this.dialog.open(ConfirmDialogComponent, {
          ...Constants.DialogOptions,
          data: {
            extra: this.gridEntityName,
            title: getStaticMessage(StaticMessage.ValidationErrorTitle),
            message: errorMessages.join('<br>'),
            buttonLabel: getStaticMessage(StaticMessage.OkButton),
            hideCancelButton: true,
          },
        });

        return false;
      }
    }

    return true;
  }

  //#endregion
}
